import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { logger } from '@app/core/helpers/logger';
import { ClassroomsService, ScreenService } from '@core/services';
import { DestroyableComponent } from '@models/destroyable.component';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { AccessStatusEnum, Classroom } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiButtonState, OnUiRadioOption } from 'onclass-ui';
import { Observable } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

const classroomAccessTypes: OnUiRadioOption[] = [
  {
    value: AccessStatusEnum.public,
    label: 'classrooms.form.access.public',
    text: 'classrooms.form.access.public-text',
  },
  {
    value: AccessStatusEnum.private,
    label: 'classrooms.form.access.private',
    text: 'classrooms.form.access.private-text',
  },
];

@Component({
  selector: 'app-classroom-form',
  templateUrl: './classroom-form.component.html',
  styleUrls: ['./classroom-form.component.scss'],
})
export class ClassroomFormComponent extends DestroyableComponent implements OnInit {
  errorNotificationService = inject(ErrorNotificationService);
  deviceService = inject(DeviceDetectorService);
  private fb = inject(UntypedFormBuilder);
  private screenService = inject(ScreenService);
  private classroomsService = inject(ClassroomsService);

  @Input()
  classroom: Classroom;

  @Output()
  saved = new EventEmitter<Classroom>();

  @Output()
  canceled = new EventEmitter<void>();

  /** Form */
  public form: UntypedFormGroup;

  /** Screen orientation */
  // TODO: отрефакторить, не использовать промежуточные константы
  public isLandscape = false;
  public isPortrait = true;
  public validationVisible = false;
  public accessTypes: OnUiRadioOption[] = classroomAccessTypes;

  /** Action buttons */
  public saveState: OnUiButtonState = 'default';

  ngOnInit(): void {
    this.createForm();
    this.patchFormValues();
    this.watchChanges();
  }

  public get isNew(): boolean {
    return this.classroom ? !this.classroom.id : true;
  }

  public get readyForSave(): boolean {
    return this.form.valid && this.saveState === 'default';
  }

  private createForm(): void {
    this.form = this.fb.group({
      participants: [[]],
      title: ['', [Validators.required, Validators.maxLength(90)]],
      description: ['', [Validators.maxLength(2000)]],
      access: [AccessStatusEnum.public, Validators.required],
      cover_id: [null],
    });
  }

  private patchFormValues(): void {
    this.form.patchValue(this.classroom);
  }

  private watchChanges(): void {
    this.watchScreenOptions$.pipe(takeUntil(this.destroyed$)).subscribe({
      error: (error) => {
        this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
      },
    });
  }

  protected get watchScreenOptions$(): Observable<any> {
    return this.screenService.options.pipe(
      tap((options) => {
        logger.log('wathc screen options');
        this.isLandscape = options.isLandscape;
        this.isPortrait = options.isPortrait;
      }),
    );
  }

  protected getValuesFromForm() {
    if (!this.readyForSave) {
      return;
    }

    const classroom = this.form.value;
    return {
      ...classroom,
      id: this.classroom ? this.classroom.id : null,
    };
  }

  public isInvalid(formControlName: string): boolean {
    return this.validationVisible && this.form?.get(formControlName)?.invalid;
  }

  public onAccessChange(type: AccessStatusEnum) {
    this.form.patchValue({ access: type });
  }

  public save() {
    this.validationVisible = true;
    const values = this.getValuesFromForm();
    logger.log('ClassroomFormComponent:save', values);
    if (!values) {
      return;
    }

    this.saveState = 'progress';
    const observable: Observable<Classroom> = this.isNew
      ? this.classroomsService.createClassroom(values)
      : this.classroomsService.updateClassroom(values.id, values);

    observable.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (classroom) => {
        this.saveState = 'done';
        this.classroom = classroom;
        this.saved.emit(classroom);
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'SAVE-PROBLEM');
        this.saveState = 'fail';
      },
    });
  }

  public cancel(): void {
    this.canceled.emit();
  }

  public trackByFn(index) {
    return index;
  }
}
